import React from 'react';
import { StructuredTaskPanel } from '../../../../common/components/StructuredTaskDetails';
import { TaskHeader } from '../../../../common/components/TaskHeader';
import { DocumentType } from '../../../../common/constants/documentType';
import { Resource } from '../../../../common/constants/resource';
import { TaskState } from '../../../../common/constants/states';
import ElementLocators from '../../../../common/ElementLocators';
import { ITask } from '../../../tasks/interfaces/ITask';
import { TRL } from '../../../tasks/types/trl';
import { valueToString } from '../../utilities/formattingUtilities';
import { factsExist } from '../../utilities/taskDetailUtilities';
import { RetryableEvidenceItem } from '../components/RetryableEvidenceItem';
import { RenderAddressSection } from './TRLAddressSection';
import { RenderApplicantSection } from './TRLApplicantSection';
import { RenderCreditSection } from './TRLCreditSection';
import { RenderEmploymentSection } from './TRLEmplolymentSection';
import { RenderScoresSection } from './TRLScoresSection';

export const TRLDetail = ({
  task,
  retryHandler,
  markOutdatedHandler,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
}: {
  task: ITask;
  retryHandler: (taskId: number) => void;
  markOutdatedHandler: (taskId: number) => void;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const { evidence } = task;

  function renderTaskDetails() {
    const trl: TRL | null = evidence ? (evidence.facts as TRL) : null;
    if (!trl) {
      return <></>;
    }
    return (
      <StructuredTaskPanel>
        <TaskHeader
          title={Resource.TASK_TITLE_TRL}
          hitTypeLabel={formatHitType(trl.applicant.hitType)}
        />
        {trl && !trl.recordExists && !evidence?.errorMessage && (
          <p>{Resource.TRL_NO_RECORD}</p>
        )}
        {trl && trl.recordExists && (
          <>
            <RenderScoresSection
              score={trl.trlScore}
              onFileSince={trl.credit.onFileSince}
            />
            <RenderApplicantSection applicant={trl.applicant} />
            <RenderCreditSection credit={trl.credit} />
            {trl.addressHistory.length > 0 && (
              <RenderAddressSection addressHistory={trl.addressHistory} />
            )}
            {trl.employmentHistory.length > 0 && (
              <RenderEmploymentSection
                employmentHistory={trl.employmentHistory}
              />
            )}
          </>
        )}
      </StructuredTaskPanel>
    );
  }

  return (
    <div data-label={ElementLocators.LOCATOR_TRL_SECTION}>
      <RetryableEvidenceItem
        task={task}
        documentType={DocumentType.TRL}
        title={Resource.TASK_TITLE_TRL}
        downloadHandler={downloadHandler}
        uploadHandler={uploadHandler}
        deleteHandler={deleteHandler}
        retryHandler={retryHandler}
        taskStateHandler={taskStateHandler}
        markOutdatedHandler={markOutdatedHandler}
      >
        {factsExist(evidence) ? renderTaskDetails() : <></>}
      </RetryableEvidenceItem>
    </div>
  );
};

function formatHitType(hitType: string) {
  switch (hitType) {
    case Resource.TRL_APPLICANT_HIT_TYPE_NO_HIT_KEY:
      return Resource.TRL_APPLICANT_HIT_TYPE_NO_HIT_VALUE;
    case Resource.TRL_APPLICANT_HIT_TYPE_REGULAR_HIT_KEY:
      return Resource.TRL_APPLICANT_HIT_TYPE_REGULAR_HIT_VALUE;
    default:
      return valueToString(hitType);
  }
}
